import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Calendar, Plus, Ban, Home, Video, Store } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

// Map friendly URL tokens → stored appointment types
const BOOK_ALIAS = {
  virtual_consult: "video_call",
  stylist_session: "shop_visit",
  video_call: "video_call",
  shop_visit: "shop_visit",
  home_visit: "home_visit",
};

const STATUS_COLORS = {
  requested: "bg-amber-100 text-amber-900",
  confirmed: "bg-emerald-100 text-emerald-900",
  rescheduled: "bg-blue-100 text-blue-900",
  completed: "bg-slate-200 text-slate-800",
  cancelled: "bg-red-100 text-red-900",
  no_show: "bg-red-100 text-red-900",
};

const TYPE_ICON = {
  shop_visit: Store,
  home_visit: Home,
  video_call: Video,
};

const TYPE_LABEL = {
  shop_visit: "Shop Visit",
  home_visit: "Home Visit",
  video_call: "Video Call",
};

export default function AppointmentsTab() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [presetType, setPresetType] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, t] = await Promise.all([
        api.get("/appointments/mine"),
        api.get("/clothing-types"),
      ]);
      setRows(a.data || []);
      setTypes(t.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Deep-link: when ?book=<type> is present, auto-open the dialog with the
  // right appointment_type selected. Cleans the query after consuming.
  useEffect(() => {
    const b = searchParams.get("book");
    if (!b) return;
    const mapped = BOOK_ALIAS[b];
    if (mapped) {
      setPresetType(mapped);
      setBookOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("book");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const typeById = useMemo(
    () => Object.fromEntries(types.map((t) => [t.id, t])),
    [types],
  );

  const cancelAppt = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try {
      await api.post(`/appointments/${id}/cancel`, {});
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="appointments-loading">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-28 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="border border-dashed border-[hsl(var(--kb-border))] p-8 text-center text-sm text-red-700" data-testid="appointments-error">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="appointments-tab">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="text-sm text-[hsl(var(--kb-muted))] max-w-xl">
          Book a shop visit, home visit, or video call with our measurement team. We'll save the final measurements to your profile automatically.
        </div>
        <button
          className="kb-btn-primary inline-flex items-center gap-2"
          onClick={() => setBookOpen(true)}
          data-testid="book-appointment-btn"
        >
          <Plus size={14} /> Book Appointment
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[hsl(var(--kb-border))] py-14 text-center" data-testid="appointments-empty">
          <Calendar size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-3" />
          <div className="font-display text-xl mb-1">No appointments yet</div>
          <div className="text-sm text-[hsl(var(--kb-muted))]">Request a measurement appointment to get perfectly-stitched pieces.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((a) => {
            const Icon = TYPE_ICON[a.appointment_type] || Calendar;
            const t = typeById[a.clothing_type_id];
            const isCancellable = !["completed", "cancelled", "no_show"].includes(a.status);
            return (
              <div
                key={a.id}
                className="border border-[hsl(var(--kb-border))] p-5"
                data-testid={`appointment-${a.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[hsl(var(--kb-surface-alt))]">
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="overline mb-0.5">{TYPE_LABEL[a.appointment_type]}</div>
                      <div className="font-display text-xl">{t?.name || "Measurement"}</div>
                      <div className="text-sm text-[hsl(var(--kb-muted))] mt-1">
                        {a.confirmed_date || a.preferred_date} · {a.confirmed_time_slot || a.preferred_time_slot}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[a.status] || "bg-slate-100"}`}
                      data-testid={`appointment-status-${a.id}`}
                    >
                      {a.status.replace(/_/g, " ")}
                    </span>
                    {isCancellable && (
                      <button
                        onClick={() => cancelAppt(a.id)}
                        className="text-xs text-red-700 hover:underline flex items-center gap-1"
                        data-testid={`cancel-appointment-${a.id}`}
                      >
                        <Ban size={12} /> Cancel
                      </button>
                    )}
                  </div>
                </div>
                {a.notes && (
                  <div className="text-xs text-[hsl(var(--kb-muted))] mt-3 italic border-t border-[hsl(var(--kb-border))]/40 pt-3">
                    "{a.notes}"
                  </div>
                )}
                {a.assigned_staff_name && (
                  <div className="text-xs mt-2">Staff: {a.assigned_staff_name}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BookingDialog open={bookOpen} onClose={() => { setBookOpen(false); setPresetType(null); }} types={types} presetType={presetType} onBooked={() => { setBookOpen(false); setPresetType(null); load(); }} />
    </div>
  );
}

function BookingDialog({ open, onClose, types, presetType, onBooked }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    clothing_type_id: "",
    appointment_type: "shop_visit",
    preferred_date: "",
    preferred_time_slot: "10:00-12:00",
    contact_number: "",
    address_line1: "",
    address_city: "",
    address_pincode: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm((s) => ({
      ...s,
      clothing_type_id: types[0]?.id || "",
      appointment_type: presetType || s.appointment_type || "shop_visit",
      preferred_date: today,
      address_line1: "",
      address_city: "",
      address_pincode: "",
      notes: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, types, presetType]);

  const book = async () => {
    if (!form.clothing_type_id) return toast.error("Select a clothing type");
    if (!form.preferred_date) return toast.error("Pick a preferred date");
    if (!/^\d{10}$/.test(form.contact_number)) return toast.error("Enter a 10-digit contact number");
    const payload = {
      clothing_type_id: form.clothing_type_id,
      appointment_type: form.appointment_type,
      preferred_date: form.preferred_date,
      preferred_time_slot: form.preferred_time_slot,
      contact_number: form.contact_number,
      notes: form.notes.trim() || null,
    };
    if (form.appointment_type === "home_visit") {
      if (!form.address_line1 || !form.address_city || !form.address_pincode) {
        return toast.error("Address is required for home visit");
      }
      payload.address = {
        line1: form.address_line1,
        city: form.address_city,
        pincode: form.address_pincode,
      };
    }
    setSaving(true);
    try {
      await api.post("/appointments", payload);
      toast.success("Appointment requested — we'll confirm soon.");
      onBooked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Book a measurement appointment</DialogTitle>
          <DialogDescription>
            We'll confirm your slot over WhatsApp/SMS within a few hours.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Clothing type</label>
              <select
                className="kb-input"
                value={form.clothing_type_id}
                onChange={(e) => setForm({ ...form, clothing_type_id: e.target.value })}
                data-testid="booking-clothing-type"
              >
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Type</label>
              <select
                className="kb-input"
                value={form.appointment_type}
                onChange={(e) => setForm({ ...form, appointment_type: e.target.value })}
                data-testid="booking-type"
              >
                <option value="shop_visit">Shop Visit</option>
                <option value="home_visit">Home Visit</option>
                <option value="video_call">Video Call</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Preferred date</label>
              <input
                type="date"
                className="kb-input"
                min={today}
                value={form.preferred_date}
                onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                data-testid="booking-date"
              />
            </div>
            <div>
              <label className="overline block mb-1">Time slot</label>
              <select
                className="kb-input"
                value={form.preferred_time_slot}
                onChange={(e) => setForm({ ...form, preferred_time_slot: e.target.value })}
                data-testid="booking-slot"
              >
                {["10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Contact number</label>
            <input
              className="kb-input"
              maxLength={10}
              placeholder="10-digit mobile"
              value={form.contact_number}
              onChange={(e) => setForm({ ...form, contact_number: e.target.value.replace(/\D/g, "").slice(0, 10) })}
              data-testid="booking-contact"
            />
          </div>

          {form.appointment_type === "home_visit" && (
            <div className="border-t pt-3 space-y-3" data-testid="booking-address-block">
              <div className="overline">Home visit address</div>
              <input
                className="kb-input"
                placeholder="Address line"
                value={form.address_line1}
                onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                data-testid="booking-address-line"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  className="kb-input"
                  placeholder="City"
                  value={form.address_city}
                  onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                  data-testid="booking-address-city"
                />
                <input
                  className="kb-input"
                  placeholder="Pincode"
                  maxLength={6}
                  value={form.address_pincode}
                  onChange={(e) => setForm({ ...form, address_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  data-testid="booking-address-pincode"
                />
              </div>
              <div className="text-xs text-[hsl(var(--kb-muted))]">A small home-visit service charge may apply — we'll confirm before your slot.</div>
            </div>
          )}

          <div>
            <label className="overline block mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              className="kb-input"
              maxLength={500}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. I prefer morning slots"
            />
          </div>
        </div>
        <DialogFooter>
          <button className="kb-btn-outline" onClick={onClose} type="button">Cancel</button>
          <button className="kb-btn-primary" onClick={book} disabled={saving} data-testid="booking-confirm-btn" type="button">
            {saving ? "Booking…" : "Request Appointment"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
